'use client'

import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import type React from 'react'
import { cn } from '../lib/utils'
import { Button } from './Button'
import { Link } from './Link'

export function Dropdown(props: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Root>) {
    return <DropdownMenuPrimitive.Root {...props} />
}

export function DropdownButton<T extends React.ElementType = typeof Button>({
    as,
    className,
    ...props
}: { className?: string; as?: T } & Omit<React.ComponentPropsWithoutRef<T>, 'className'>) {
    const Component = (as ?? Button) as React.ElementType
    return (
        <DropdownMenuPrimitive.Trigger asChild>
            <Component className={className} {...props} />
        </DropdownMenuPrimitive.Trigger>
    )
}

type LegacyAnchor =
    | 'top' | 'top start' | 'top end'
    | 'bottom' | 'bottom start' | 'bottom end'
    | 'left' | 'left start' | 'left end'
    | 'right' | 'right start' | 'right end'

function parseAnchor(anchor: LegacyAnchor): { side: 'top' | 'right' | 'bottom' | 'left'; align: 'start' | 'center' | 'end' } {
    const [side, align] = anchor.split(' ') as [any, any]
    return { side, align: align ?? 'center' }
}

export function DropdownMenu({
    anchor,
    side,
    align,
    className,
    ...props
}: {
    className?: string
    /** @deprecated Headless UI-style anchor string, kept for backward compatibility — prefer `side`/`align`. */
    anchor?: LegacyAnchor
} & Omit<React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>, 'side' | 'align'> &
    Partial<Pick<React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>, 'side' | 'align'>>) {
    const resolved = anchor ? parseAnchor(anchor) : { side: side ?? 'bottom', align: align ?? 'start' }

    return (
        <DropdownMenuPrimitive.Portal>
            <DropdownMenuPrimitive.Content
                sideOffset={8}
                side={resolved.side}
                align={resolved.align}
                {...props}
                className={cn(
                    // Base styles
                    'isolate w-max min-w-[--radix-dropdown-menu-trigger-width] rounded-xl p-1',
                    // Invisible border that is only visible in `forced-colors` mode for accessibility purposes
                    'outline outline-1 outline-transparent',
                    // Never exceed the viewport, however wide a custom className asks for
                    'max-w-[--radix-dropdown-menu-content-available-width]',
                    // Handle scrolling when menu won't fit in viewport
                    'max-h-[--radix-dropdown-menu-content-available-height] overflow-y-auto',
                    // Popover background
                    'bg-white/75 backdrop-blur-xl dark:bg-zinc-800/75',
                    // Shadows
                    'shadow-lg ring-1 ring-zinc-950/10 dark:ring-inset dark:ring-white/10',
                    // Grid so icon/label/description/shortcut line up across items
                    'grid grid-cols-[auto_1fr_1.5rem_0.5rem_auto]',
                    // Transitions
                    'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
                    className
                )}
            />
        </DropdownMenuPrimitive.Portal>
    )
}

export function DropdownItem({
    className,
    ...props
}: { className?: string } & (
    | Omit<React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item>, 'className'>
    | Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'>
)) {
    const classes = cn(
        // Base styles
        'group cursor-default rounded-lg px-3.5 py-2.5 focus:outline-none sm:px-3 sm:py-1.5',
        // Text styles
        'text-left text-base/6 text-zinc-950 sm:text-sm/6 dark:text-white forced-colors:text-[CanvasText]',
        // Focus/highlighted
        'data-[highlighted]:bg-primary data-[highlighted]:text-primary-foreground',
        // Disabled state
        'data-[disabled]:opacity-50',
        // Forced colors mode
        'forced-color-adjust-none forced-colors:data-[highlighted]:bg-[Highlight] forced-colors:data-[highlighted]:text-[HighlightText] forced-colors:[&>[data-slot=icon]]:data-[highlighted]:text-[HighlightText]',
        // Use subgrid when available but fallback to an explicit grid layout if not
        'col-span-full grid grid-cols-[auto_1fr_1.5rem_0.5rem_auto] items-center supports-[grid-template-columns:subgrid]:grid-cols-subgrid',
        // Icons
        '[&>[data-slot=icon]]:col-start-1 [&>[data-slot=icon]]:row-start-1 [&>[data-slot=icon]]:-ml-0.5 [&>[data-slot=icon]]:mr-2.5 [&>[data-slot=icon]]:size-5 sm:[&>[data-slot=icon]]:mr-2 [&>[data-slot=icon]]:sm:size-4',
        '[&>[data-slot=icon]]:text-zinc-500 [&>[data-slot=icon]]:data-[highlighted]:text-primary-foreground [&>[data-slot=icon]]:dark:text-zinc-400',
        // Avatar
        '[&>[data-slot=avatar]]:-ml-1 [&>[data-slot=avatar]]:mr-2.5 [&>[data-slot=avatar]]:size-6 sm:[&>[data-slot=avatar]]:mr-2 sm:[&>[data-slot=avatar]]:size-5',
        className
    )

    return 'href' in props ? (
        <DropdownMenuPrimitive.Item asChild>
            <Link {...props} className={classes} />
        </DropdownMenuPrimitive.Item>
    ) : (
        <DropdownMenuPrimitive.Item {...props} className={classes} />
    )
}

export function DropdownHeader({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
    return <div {...props} className={cn('col-span-5 px-3.5 pb-1 pt-2.5 sm:px-3', className)} />
}

export function DropdownSection({
    className,
    ...props
}: { className?: string } & React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Group>) {
    return (
        <DropdownMenuPrimitive.Group
            {...props}
            className={cn(
                'col-span-full grid grid-cols-[auto_1fr_1.5rem_0.5rem_auto] supports-[grid-template-columns:subgrid]:grid-cols-subgrid',
                className
            )}
        />
    )
}

export function DropdownHeading({
    className,
    ...props
}: { className?: string } & React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>) {
    return (
        <DropdownMenuPrimitive.Label
            {...props}
            className={cn(
                'col-span-full grid grid-cols-[1fr,auto] gap-x-12 px-3.5 pb-1 pt-2 text-sm/5 font-medium text-zinc-500 sm:px-3 sm:text-xs/5 dark:text-zinc-400',
                className
            )}
        />
    )
}

export function DropdownDivider({
    className,
    ...props
}: { className?: string } & React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>) {
    return (
        <DropdownMenuPrimitive.Separator
            {...props}
            className={cn(
                'col-span-full mx-3.5 my-1 h-px border-0 bg-zinc-950/5 sm:mx-3 dark:bg-white/10 forced-colors:bg-[CanvasText]',
                className
            )}
        />
    )
}

export function DropdownLabel({ className, ...props }: React.ComponentPropsWithoutRef<'span'>) {
    return <span {...props} data-slot="label" className={cn('col-start-2 row-start-1', className)} />
}

export function DropdownDescription({ className, ...props }: React.ComponentPropsWithoutRef<'span'>) {
    return (
        <span
            {...props}
            data-slot="description"
            className={cn(
                'col-span-2 col-start-2 row-start-2 text-sm/5 text-zinc-500 group-data-[highlighted]:text-primary-foreground sm:text-xs/5 dark:text-zinc-400 forced-colors:group-data-[highlighted]:text-[HighlightText]',
                className
            )}
        />
    )
}

export function DropdownShortcut({
    keys,
    className,
    ...props
}: { keys: string | string[]; className?: string } & React.ComponentPropsWithoutRef<'kbd'>) {
    return (
        <kbd {...props} className={cn('col-start-5 row-start-1 flex justify-self-end', className)}>
            {(Array.isArray(keys) ? keys : keys.split('')).map((char, index) => (
                <kbd
                    key={index}
                    className={cn(
                        'min-w-[2ch] text-center font-sans capitalize text-zinc-400 group-data-[highlighted]:text-primary-foreground forced-colors:group-data-[highlighted]:text-[HighlightText]',
                        // Make sure key names that are longer than one character (like "Tab") have extra space
                        index > 0 && char.length > 1 && 'pl-1'
                    )}
                >
                    {char}
                </kbd>
            ))}
        </kbd>
    )
}
